import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  Alert,
  Container,
  CircularProgress,
  useTheme,
} from "@mui/material";
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import RiskAssessmentTable from "./RiskAssessmentTable";
import { getAssessmentStatusChip, getRiskLevel } from "../../utils/riskUtils";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_RISK_ASSESSMENT_ITEMS_BY_ID,
  GET_RISKS_FROM_REGISTER,
  DELETE_DRAFT_ASSESSMENT,
  COMPLETE_RISK_ASSESSMENT,
} from "../../../../../apis/organization-apis/risk-apis/riskApis";
import RiskDetailsDialog from "../../components/Dialogs/RiskDetailDialog/RiskDetailsDialog";
import ConfirmSnackbar from "../../../../../components/Snackbar/ConfirmSnackbar";
import RiskSummary from "../../pages/RiskSummary";
import HighResidualRiskDialog from "../../components/Dialogs/HighResidualRiskDialog/HighResidualRiskDialog";
import {
  RiskAssessmentStatusTitle,
  RiskLevels,
  TabName,
} from "../../utils/enums";
import TableSkeleton from "../../../../../components/Skeletons/TableSkeleton";
import { formatDate } from "../../../../../utils/dateUtils";
import { exportRiskAssessmentToExcel } from "../../utils/exportRiskAssessmentToExcel";
import AccessControl, {
  FEATURE_IDS,
} from "../../../../../components/AccessControl";

const RiskAssessment = ({
  allRiskAssessments,
  allRiskAssessmentsLoading,
  refetchAssessments,
  onStartAssessment,
}) => {
  const theme = useTheme();
  const [selectedAssessmentIdx, setSelectedAssessmentIdx] = useState(0);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [assessmentSnackbar, setAssessmentSnackbar] = useState(null);
  const [deleteDraftAssessment, { loading: deleteDraftAssessmentLoading }] =
    useMutation(DELETE_DRAFT_ASSESSMENT);
  const [completeAssessment, { loading: completeAssessmentLoading }] =
    useMutation(COMPLETE_RISK_ASSESSMENT);

  const [isHighResidualRiskDialog, setIsHighResidualRiskDialog] =
    useState(false);
  const [missingCommentsRisks, setMissingCommentsRisks] = useState([]);
  const [missingCommentsReasons, setMissingCommentsReasons] = useState({});

  const assessments = allRiskAssessments?.getAllRiskAssessments?.data || [];
  const assessmentsLoading = allRiskAssessmentsLoading;
  const assessmentsError = allRiskAssessments?.error;

  const currentAssessment = assessments[selectedAssessmentIdx];
  const assessmentStatusChip = currentAssessment
    ? getAssessmentStatusChip(currentAssessment.status_title)
    : null;

  const {
    data: itemsData,
    loading: itemsLoading,
    error: itemsError,
    refetch: refetchItems,
  } = useQuery(GET_RISK_ASSESSMENT_ITEMS_BY_ID, {
    variables: { assessment_id: currentAssessment?.id },
    skip: !currentAssessment?.id,
  });

  const { data: registerRisksData } = useQuery(GET_RISKS_FROM_REGISTER, {
    variables: {
      filter: {},
      page: 1,
      limit: 1000,
    },
  });

  const liveOwnerByRiskId = useMemo(() => {
    const ownerByRiskId = new Map();
    for (const registerRisk of registerRisksData?.getRisks?.data || []) {
      ownerByRiskId.set(Number(registerRisk?.id), registerRisk?.risk_owner_email);
    }
    return ownerByRiskId;
  }, [registerRisksData]);

  const risks = useMemo(() => {
    const items = itemsData?.getRiskAssessmentItemsById?.data || [];
    return items.map((item) => ({
      ...item,
      risk_owner_email:
        liveOwnerByRiskId.get(Number(item?.original_risk_id)) ??
        item?.risk_owner_email,
    }));
  }, [itemsData, liveOwnerByRiskId]);

  const getIconComponent = (iconName) => {
    switch (iconName) {
      case "ScheduleIcon":
        return <ScheduleIcon sx={{ fontSize: 16 }} />;
      case "CheckCircleIcon":
        return <CheckCircleIcon sx={{ fontSize: 16 }} />;
      default:
        return null;
    }
  };

  const handleExportToExcel = () => {
    if (risks?.length > 0) {
      exportRiskAssessmentToExcel(risks, currentAssessment);
    }
  };

  const handleViewDetails = (risk) => {
    if (risk) {
      setSelectedRisk(risk);
      setIsDetailDialogOpen(true);
    }
  };

  const handleDetailDialogClose = () => {
    setIsDetailDialogOpen(false);
    setSelectedRisk(null);
  };

  const handleSubmitAssessment = async () => {
    try {
      const { data } = await completeAssessment({
        variables: { id: currentAssessment.id },
      });
      if (data?.completeRiskAssessment?.success) {
        setAssessmentSnackbar({
          message: "Assessment submitted!",
          vertical: "top",
          horizontal: "right",
        });
        refetchAssessments();
        refetchItems();
        handleDialogClose(true);
      } else {
        setAssessmentSnackbar({
          message:
            data?.completeRiskAssessment?.message ||
            "Failed to submit assessment",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (err) {
      setAssessmentSnackbar({
        message: `Error submitting assessment: ${err.message}`,
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  const handleDiscardAssessment = async (assessmentId) => {
    try {
      const { data } = await deleteDraftAssessment({
        variables: { id: assessmentId },
      });
      if (data?.deleteDraftAssessment?.success) {
        setAssessmentSnackbar({
          message: "Draft assessment deleted!",
          vertical: "top",
          horizontal: "right",
        });
        refetchAssessments();
        refetchItems();
      } else {
        setAssessmentSnackbar({
          message:
            data?.deleteDraftAssessment?.message ||
            "Failed to delete draft assessment",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (err) {
      setAssessmentSnackbar({
        message: `Error deleting draft assessment: ${err.message}`,
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  const handleDialogClose = (success) => {
    if (success) {
      setMissingCommentsRisks([]);
      setMissingCommentsReasons({});
    }
    setIsHighResidualRiskDialog(false);
    refetchItems();
  };

  const checkForHighRiskNoComments = () => {
    const highRiskNoComments = risks?.filter((risk) => {
      const level = getRiskLevel(risk?.residual_risk_score);
      const isHighRisk = level === RiskLevels.HIGH;
      const hasNoComments = !risk?.comments || risk.comments.trim() === "";
      return isHighRisk && hasNoComments;
    });

    if (highRiskNoComments?.length > 0) {
      setMissingCommentsRisks(highRiskNoComments);
      setMissingCommentsReasons({});
      setIsHighResidualRiskDialog(true);
      return true;
    }
    return false;
  };

  return (
    <>
      <ConfirmSnackbar snackbarInfo={assessmentSnackbar} />
      <RiskSummary risks={risks} loading={assessmentsLoading || itemsLoading} />
      <Card
        sx={{
          minHeight: "600px",
          borderRadius: "12px",
          boxShadow: theme.customColors.shadow.main,
          border: `1px solid ${theme.customColors.border.main}`,
          overflow: "hidden",
          backgroundColor: theme.customColors.background.main,
        }}
        variant="outlined"
      >
        {assessmentsError ? (
          <Container
            component="main"
            maxWidth="xs"
            sx={{ textAlign: "center", py: 12 }}
          >
            <Typography
              variant="h5"
              color={theme.customColors.status.error.text}
              sx={{ mb: 2 }}
            >
              Error Loading Assessments
            </Typography>
          </Container>
        ) : assessmentsLoading ? (
          <TableSkeleton />
        ) : (
          <CardContent sx={{ p: 0 }}>
            {assessments?.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8, px: 4 }}>
                <WarningIcon
                  sx={{
                    fontSize: 40,
                    color: theme.customColors.text.secondary,
                    mb: 2,
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{ mb: 1, color: theme.customColors.text.secondary }}
                >
                  No assessments started yet
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mb: 3, color: theme.customColors.text.secondary }}
                >
                  Start an assessment to view assessment data
                </Typography>
                {onStartAssessment && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={onStartAssessment}
                    sx={{
                      bgcolor: theme.customColors.button.primary,
                      color: theme.customColors.button.text,
                      fontWeight: 500,
                      borderRadius: "8px",
                      textTransform: "none",
                      fontSize: "14px",
                      px: 2,
                      py: 1,
                      "&:hover": { bgcolor: theme.palette._blue.dark },
                    }}
                  >
                    Start Assessment
                  </Button>
                )}
              </Box>
            ) : (
              <Stack spacing={3} sx={{ p: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="assessment-select-label">
                    Select Assessment
                  </InputLabel>
                  <Select
                    labelId="assessment-select-label"
                    value={selectedAssessmentIdx}
                    label="Select Assessment"
                    onChange={(e) => setSelectedAssessmentIdx(e.target.value)}
                  >
                    {assessments?.map((assessment, idx) => (
                      <MenuItem value={idx} key={assessment?.id || idx}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "100%",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: theme.customColors.text.strong }}
                          >
                            {assessment?.title} -{" "}
                            {formatDate(assessment?.created_at)}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: theme.customColors.text.secondary }}
                          >
                            {assessment?.status_title}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Card
                  variant="outlined"
                  sx={{
                    backgroundColor: theme.customColors.background.cardHeader,
                    border: `1px solid ${theme.customColors.border.subtle}`,
                    borderRadius: "10px",
                  }}
                >
                  <CardContent
                    sx={{
                      py: 2,
                      px: 2.5,
                      "&:last-child": { pb: 2 },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        flexWrap: "wrap",
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.25,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            fontWeight={600}
                            sx={{
                              color: theme.customColors.text.strong,
                              lineHeight: 1.4,
                            }}
                          >
                            {currentAssessment?.status_title ===
                            RiskAssessmentStatusTitle.COMPLETED
                              ? `Assessment approved at ${formatDate(
                                  currentAssessment?.completed_at
                                )}`
                              : currentAssessment?.status_title ===
                                  RiskAssessmentStatusTitle.DISCARDED
                                ? `Assessment discarded at ${formatDate(
                                    currentAssessment?.updated_at
                                  )}`
                                : `Assessment started at ${formatDate(
                                    currentAssessment?.created_at
                                  )}`}
                          </Typography>
                          {assessmentStatusChip && (
                            <Chip
                              color={assessmentStatusChip.color}
                              icon={getIconComponent(assessmentStatusChip.icon)}
                              label={assessmentStatusChip.label}
                              size="small"
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                borderRadius: "6px",
                                border: "none",
                                height: 24,
                                backgroundColor:
                                  assessmentStatusChip.color === "warning"
                                    ? theme.customColors.status.warning.bg
                                    : assessmentStatusChip.color === "success"
                                      ? theme.customColors.status.success.bg
                                      : theme.customColors.status.error.bg,
                                color:
                                  assessmentStatusChip.color === "warning"
                                    ? theme.customColors.status.warning.text
                                    : assessmentStatusChip.color === "success"
                                      ? theme.customColors.status.success.text
                                      : theme.customColors.status.error.text,
                                "& .MuiChip-icon": {
                                  color: "inherit",
                                  fontSize: "16px",
                                },
                              }}
                            />
                          )}
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 0.75,
                            mt: 0.75,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: theme.customColors.text.secondary }}
                          >
                            Created by:{" "}
                            {currentAssessment?.assessor_email || "-"}
                          </Typography>
                          {currentAssessment?.status_title ===
                            RiskAssessmentStatusTitle.COMPLETED && (
                            <>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: theme.customColors.text.secondary,
                                  px: 0.25,
                                }}
                              >
                                ·
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: theme.customColors.text.secondary,
                                }}
                              >
                                Approved by:{" "}
                                {currentAssessment?.approver_email || "-"}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Box>
                      <AccessControl
                        featureId={FEATURE_IDS.RISK_ASSESSMENT_EXCEL_EXPORT}
                      >
                        <Button
                          variant="outlined"
                          startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                          onClick={handleExportToExcel}
                          disabled={!risks?.length || itemsLoading}
                          sx={{
                            flexShrink: 0,
                            fontWeight: 500,
                            borderRadius: "8px",
                            textTransform: "none",
                            fontSize: "14px",
                            px: 2,
                            py: 0.75,
                            backgroundColor:
                              theme.customColors.background.main,
                            borderColor: theme.customColors.border.main,
                            color: theme.customColors.text.strong,
                            "&:hover": {
                              borderColor: theme.customColors.border.strong,
                              backgroundColor:
                                theme.customColors.background.hover,
                            },
                            "&:disabled": {
                              borderColor:
                                theme.customColors.border.disabled,
                              color: theme.customColors.text.disabled,
                            },
                          }}
                        >
                          Export
                        </Button>
                      </AccessControl>
                    </Box>
                  </CardContent>
                </Card>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  {itemsError ? (
                    <Container
                      component="main"
                      maxWidth="xs"
                      sx={{ textAlign: "center", py: 12 }}
                    >
                      <Typography
                        variant="h5"
                        color={theme.customColors.status.error.text}
                        sx={{ mb: 2 }}
                      >
                        Error Loading Risks
                      </Typography>
                    </Container>
                  ) : itemsLoading ? (
                    <TableSkeleton />
                  ) : (
                    <RiskAssessmentTable
                      risks={risks}
                      onViewDetails={handleViewDetails}
                      refetch={refetchItems}
                    />
                  )}

                  {!itemsLoading && (
                    <>
                      {currentAssessment?.status_title ===
                        RiskAssessmentStatusTitle.DRAFT && (
                          <Alert
                            severity="warning"
                            sx={{
                              mt: 1,
                              borderRadius: "8px",
                              border: `1px solid ${theme.customColors.status.warning.bg}`,
                              backgroundColor:
                                theme.customColors.status.warning.bg,
                              display: "flex",
                              alignItems: "center",
                              "& .MuiAlert-icon": {
                                color: theme.customColors.status.warning.main,
                              },
                              "& .MuiAlert-message": {
                                color: theme.customColors.status.warning.text,
                                fontWeight: 500,
                                fontSize: "14px",
                                display: "flex",
                                alignItems: "center",
                              },
                            }}
                            action={
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1.5,
                                  alignItems: "center",
                                }}
                              >
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
                                  onClick={() => {
                                    if (!checkForHighRiskNoComments()) {
                                      handleSubmitAssessment();
                                    }
                                  }}
                                  sx={{
                                    backgroundColor:
                                      theme.customColors.button.dark,
                                    color: theme.customColors.button.text,
                                    fontWeight: 500,
                                    borderRadius: "8px",
                                    textTransform: "none",
                                    fontSize: "14px",
                                    px: 2,
                                    py: 1,
                                    "&:hover": {
                                      backgroundColor:
                                        theme.customColors.button.hover,
                                    },
                                    "&:disabled": {
                                      backgroundColor:
                                        theme.customColors.button.dark,
                                      opacity: 0.5,
                                    },
                                  }}
                                >
                                  Submit
                                  {completeAssessmentLoading && (
                                    <CircularProgress
                                      size={20}
                                      sx={{
                                        color: theme.customColors.button.text,
                                        ml: 1,
                                      }}
                                    />
                                  )}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                                  onClick={() =>
                                    handleDiscardAssessment(currentAssessment.id)
                                  }
                                  sx={{
                                    fontWeight: 500,
                                    borderRadius: "8px",
                                    textTransform: "none",
                                    fontSize: "14px",
                                    px: 2,
                                    py: 1,
                                    borderColor:
                                      theme.customColors.status.error.main,
                                    color: theme.customColors.status.error.main,
                                    "&:hover": {
                                      borderColor:
                                        theme.customColors.status.error.text,
                                      backgroundColor:
                                        theme.customColors.status.error.bg,
                                      color: theme.customColors.status.error.text,
                                    },
                                    "&:disabled": {
                                      borderColor:
                                        theme.customColors.status.error.main,
                                      opacity: 0.5,
                                    },
                                  }}
                                >
                                  Discard
                                  {deleteDraftAssessmentLoading && (
                                    <CircularProgress
                                      size={20}
                                      sx={{
                                        color:
                                          theme.customColors.status.error.main,
                                        ml: 1,
                                      }}
                                    />
                                  )}
                                </Button>
                              </Box>
                            }
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                fontSize: "14px",
                                lineHeight: 1.4,
                              }}
                            >
                              This assessment is pending review. Submit to approve
                              or discard to remove.
                            </Typography>
                          </Alert>
                        )}

                      {currentAssessment?.status_title ===
                        RiskAssessmentStatusTitle.COMPLETED && (
                          <Alert
                            severity="success"
                            sx={{
                              mt: 1,
                              borderRadius: "8px",
                              border: `1px solid ${theme.customColors.status.success.bg}`,
                              backgroundColor:
                                theme.customColors.status.success.bg,
                              display: "flex",
                              alignItems: "center",
                              "& .MuiAlert-icon": {
                                color: theme.customColors.status.success.main,
                              },
                              "& .MuiAlert-message": {
                                color: theme.customColors.status.success.text,
                                fontWeight: 500,
                                fontSize: "14px",
                                display: "flex",
                                alignItems: "center",
                              },
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                fontSize: "14px",
                                lineHeight: 1.4,
                              }}
                            >
                              This assessment has been reviewed and approved.
                            </Typography>
                          </Alert>
                        )}

                      {currentAssessment?.status_title ===
                        RiskAssessmentStatusTitle.DISCARDED && (
                          <Alert
                            severity="error"
                            sx={{
                              mt: 1,
                              borderRadius: "8px",
                              border: `1px solid ${theme.customColors.status.error.bg}`,
                              backgroundColor: theme.customColors.status.error.bg,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                fontSize: "14px",
                                lineHeight: 1.4,
                              }}
                            >
                              This assessment has been discarded.
                            </Typography>
                          </Alert>
                        )}
                    </>
                  )}
                </Box>
              </Stack>
            )}
          </CardContent>
        )}
      </Card>

      {isDetailDialogOpen && (
        <RiskDetailsDialog
          open={isDetailDialogOpen}
          onClose={handleDetailDialogClose}
          risk={selectedRisk}
          refetchItems={refetchItems}
          type={TabName.ASSESSMENT}
          isEditable={
            currentAssessment?.status_title === RiskAssessmentStatusTitle.DRAFT
          }
        />
      )}

      {isHighResidualRiskDialog && (
        <HighResidualRiskDialog
          open={isHighResidualRiskDialog}
          onClose={(success) => handleDialogClose(success)}
          missingCommentsRisks={missingCommentsRisks}
          missingCommentsReasons={missingCommentsReasons}
          setMissingCommentsReasons={setMissingCommentsReasons}
          completeAssessment={handleSubmitAssessment}
        />
      )}
    </>
  );
};

export default RiskAssessment;
